import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { VirtualizedState } from './virtualized-state';
import { measureVirtualizedNodes } from './measure-virtualized-nodes';
import produce from 'immer';

/**
 * React hook which measures virtualized nodes after they're rendered to the
 * DOM.
 * @param scrollableRef Ref to the parent scrollable container.
 * @param containerRef Container ref.
 * @param state Current virtualized state.
 * @param direction Virtualized direction.
 */
export const useVirtualizedMeasurer = (
  scrollableRef: React.RefObject<any> | React.MutableRefObject<any>,
  containerRef: React.RefObject<any> | React.MutableRefObject<any>,
  state: VirtualizedState,
  direction: 'vertical' | 'horizontal',
): void => {
  const {
    virtualizedChildren,
    childSizes,
    currentSlice,
    setChildSizes,
  } = state;

  const firstRender = useRef(true);

  // Callback executed to measure the rendered virtualized nodes.
  const measureChildren = useCallback((): void => {
    const scrollable = scrollableRef.current;
    const container = containerRef.current;

    // If there's no current for either element, then stop here. We can't
    // continue. (This shouldn't ever happen.)
    if (!scrollable || !container) {
      return;
    }

    const virtualizedNodes = Array.from<HTMLElement>(container.children as any);

    const measurements = measureVirtualizedNodes(
      container,
      virtualizedNodes,
      direction,
    );

    const nextChildSizes = produce(childSizes, (draft): void => {
      for (let i = 0; i < measurements.length; i++) {
        draft[i + currentSlice.start] = measurements[i].height;
      }
    });

    if (nextChildSizes !== childSizes) {
      setChildSizes(nextChildSizes);
    }
  }, [childSizes, currentSlice.start, currentSlice.end]);

  // For the initial render, use `LayoutEffect` to avoid a flash of incorrectly
  // styled components.
  // useLayoutEffect((): void => {
  //   if (firstRender.current) {
  //     measureChildren();
  //   }
  // }, [virtualizedChildren]);

  useEffect((): void => {
    // if (!firstRender.current) {
    //   measureChildren();
    // }
    measureChildren();
    firstRender.current = false;
  }, [virtualizedChildren]);
};
